import { useMemo } from 'react';

import { useBackerHasStudentFeesAccess } from '@givebacks/api/backer';
import { useUserData } from '@givebacks/api/core';

import { useInternalToolsMenu } from '../../internalTools/useInternalMenu';
import { UserFeatureFlags } from '../flags.types';

type AccessFeatures = UserFeatureFlags;

const useUserReleaseControl = ({ feature }: { feature: AccessFeatures }) => {
  const {
    hasStudentFeesAccess,
    isLoading: isStudentFeesActivelyLoading,
    isInitialLoading: isStudentFeesInitialLoading,
  } = useBackerHasStudentFeesAccess();
  const { getToolValue, isToolsLoading } = useInternalToolsMenu();

  const isStudentFeesLoading = useMemo(() => {
    return isStudentFeesActivelyLoading || isStudentFeesInitialLoading;
  }, [isStudentFeesActivelyLoading, isStudentFeesInitialLoading]);

  const { data: hasAccountPageBeta, isLoading: isAccountPageBetaLoading } = useUserData(
    (user) => user.release.account_page.beta
  );

  const status = useMemo(() => {
    // If we are in a non-production environment and the feature flag override is enabled, return true
    if (import.meta.env.VITE_API_ENV !== 'production' && getToolValue('feature-flag-override')) {
      return true;
    }

    switch (feature) {
      case 'user.studentFees':
        return hasStudentFeesAccess;
      case 'user.account_page':
        return hasAccountPageBeta;
      case 'user.consolidatedCheckout':
        // Never return true as this is a temporary feature flag to allow developing the consolidated checkout experience.
        return false;
      default:
        return false;
    }
  }, [feature, getToolValue, hasStudentFeesAccess, hasAccountPageBeta]);

  const isLoading = useMemo(
    () => isStudentFeesLoading || isToolsLoading || isAccountPageBetaLoading,
    [isToolsLoading, isStudentFeesLoading, isAccountPageBetaLoading]
  );

  return { accessStatus: status, isLoading };
};

export { useUserReleaseControl };
